import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SectionGuide } from '../../components/pas/SectionGuide';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import { ScanLine, Store, CheckCircle2, XCircle, ArrowLeft, Camera, ExternalLink } from 'lucide-react';
import { usePASClientAuth } from '../../context/PASClientAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-pos`;
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Scanner = ({ onCode }) => {
  const [on, setOn] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!on) return undefined;
    const qr = new Html5Qrcode('pos-qr-reader');
    ref.current = qr;
    qr.start({ facingMode: 'environment' }, { fps: 10, qrbox: 240 }, (text) => { onCode(text); setOn(false); }, () => {})
      .catch(() => { toast.error('Camera not available. Paste the check link instead.'); setOn(false); });
    return () => { qr.isScanning && qr.stop().catch(() => {}); };
  }, [on, onCode]);
  return (
    <div>
      <div id="pos-qr-reader" className={on ? 'rounded-xl overflow-hidden mb-3' : 'hidden'} data-testid="pos-qr-reader" />
      <button onClick={() => setOn(!on)} data-testid="pos-scan-btn"
        className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">
        <Camera className="w-5 h-5" /> {on ? 'Stop camera' : 'Scan check QR'}
      </button>
    </div>
  );
};

const CheckResult = ({ c, busy, onAccept }) => (
  <div className="bg-slate-900 border border-slate-700 rounded-xl p-5" data-testid="pos-check-result">
    <div className="flex items-center justify-between mb-3">
      <p className="text-white font-semibold">Check No. {c.check_no}</p>
      {c.blocked_reason ? <span className="text-red-400 text-sm flex items-center gap-1"><XCircle className="w-4 h-4" /> Not acceptable</span>
        : <span className="text-emerald-400 text-sm flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Genuine and unclaimed</span>}
    </div>
    <p className="text-3xl font-bold text-white mb-1" data-testid="pos-check-amount">{usd(c.amount)}</p>
    <p className="text-sm text-slate-400 mb-4">{c.token_symbol === 'BETH' ? 'BETH USD stablecoin' : 'BTCK'} on {c.network} · payee {c.payee || '-'} · issued {c.date}</p>
    {c.blocked_reason ? <p className="text-sm text-red-300" data-testid="pos-blocked-reason">{c.blocked_reason}</p> : (
      <button onClick={onAccept} disabled={busy} data-testid="pos-accept-btn"
        className="w-full py-3 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-900 rounded-xl font-bold transition-colors">
        {busy ? 'Moving check on-chain…' : `Accept ${usd(c.amount)} into my wallet`}
      </button>
    )}
  </div>
);

export default function PASPointOfSale() {
  const { token } = usePASClientAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadHistory = useCallback(() => {
    axios.get(`${API}/history`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setHist(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { if (!token) navigate('/pas/login?next=/pas/pos'); else loadHistory(); }, [token, navigate, loadHistory]);

  const lookup = useCallback(async (value) => {
    setCode(value); setCheck(null);
    try { setCheck((await axios.post(`${API}/lookup`, { code: value }, { headers: { Authorization: `Bearer ${token}` } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Check not found'); }
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/accept`, { code }, auth);
      toast.success(r.data.message); setCheck(null); setCode(''); loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not accept check'); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-8" data-testid="pos-page">
      <div className="max-w-xl mx-auto space-y-6">
        <button onClick={() => navigate('/pas/dashboard')} className="text-slate-400 text-sm flex items-center gap-1" data-testid="pos-back-btn"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3"><Store className="w-8 h-8 text-emerald-400" /> Point of Sale</h1>
        </div>
        <SectionGuide id="pos" />
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-900 rounded-xl p-4 border border-slate-800"><p className="text-xs text-slate-400">Accepted today</p><p className="text-2xl font-bold" data-testid="pos-today-total">{usd(hist?.today_total)}</p></div>
          <div className="bg-slate-900 rounded-xl p-4 border border-slate-800"><p className="text-xs text-slate-400">Checks today</p><p className="text-2xl font-bold" data-testid="pos-today-count">{hist?.today_count || 0}</p></div>
        </div>
        <Scanner onCode={lookup} />
        <form onSubmit={(e) => { e.preventDefault(); lookup(code); }} className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="…or paste the check link" data-testid="pos-code-input"
            className="flex-1 px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-sm" />
          <button type="submit" data-testid="pos-lookup-btn" className="px-4 bg-slate-700 hover:bg-slate-600 rounded-xl flex items-center gap-1 text-sm"><ScanLine className="w-4 h-4" /> Check</button>
        </form>
        {check && <CheckResult c={check} busy={busy} onAccept={accept} />}
        <div data-testid="pos-history">
          <p className="text-sm text-slate-300 font-medium mb-2">Recent acceptances</p>
          {!hist?.acceptances?.length ? <p className="text-xs text-slate-500">None yet.</p> : hist.acceptances.slice(0, 20).map(a => (
            <div key={a.id} className="flex items-center justify-between py-2 border-b border-slate-800 text-sm" data-testid={`pos-history-${a.check_no}`}>
              <span>No. {a.check_no} · {new Date(a.accepted_at).toLocaleString()}</span>
              <a href={a.explorer_url} target="_blank" rel="noreferrer" className="text-emerald-400 flex items-center gap-1">{usd(a.amount)} <ExternalLink className="w-3 h-3" /></a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
